/**
 * Confluence Setup Component
 * Allows admins to configure Confluence SSO authentication
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Article as ArticleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
  Cookie as CookieIcon,
} from '@mui/icons-material';
import { integrationsApi, ConfluenceSSOStatusResponse } from '@/services/api';

export default function ConfluenceSetup() {
  const queryClient = useQueryClient();
  const [cookieDialogOpen, setCookieDialogOpen] = useState(false);
  const [cookieInput, setCookieInput] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);

  // Query Confluence SSO status
  const { data: ssoStatus, isLoading: statusLoading, refetch: refetchStatus } = useQuery({
    queryKey: ['confluence-sso-status'],
    queryFn: () => integrationsApi.confluenceAuthStatus().then((res) => res.data),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Mutation to save cookies
  const saveCookiesMutation = useMutation({
    mutationFn: (cookies: Record<string, string>) =>
      integrationsApi.confluenceSaveCookies(cookies).then((res) => res.data),
    onSuccess: (data) => {
      setSaveError(null);
      setCookieDialogOpen(false);
      setCookieInput('');
      refetchStatus();
      queryClient.invalidateQueries({ queryKey: ['integrations-status'] });

      if (!data.success) {
        setSaveError(data.message);
      }
    },
    onError: (error: Error) => {
      console.error('Save cookies error:', error);
      setSaveError(error.message || 'Failed to save cookies');
    },
  });

  // Mutation to clear cookies
  const clearCookiesMutation = useMutation({
    mutationFn: () => integrationsApi.confluenceClearCookies(),
    onSuccess: () => {
      refetchStatus();
      queryClient.invalidateQueries({ queryKey: ['integrations-status'] });
    },
    onError: (error: Error) => {
      console.error('Clear cookies error:', error);
      setSaveError(error.message || 'Failed to clear cookies');
    },
  });

  const handleSaveCookies = () => {
    try {
      // Parse the JSON input
      const cookies = JSON.parse(cookieInput);
      if (typeof cookies !== 'object' || Array.isArray(cookies)) {
        setSaveError('Please enter a valid JSON object with cookie name-value pairs');
        return;
      }
      saveCookiesMutation.mutate(cookies);
    } catch {
      setSaveError('Invalid JSON format. Please enter valid JSON, e.g.: {"JSESSIONID": "...", "seraph.confluence": "..."}');
    }
  };

  const handleOpenConfluence = () => {
    if (ssoStatus?.base_url) {
      window.open(ssoStatus.base_url, '_blank');
    }
  };

  const getStatusColor = (status: ConfluenceSSOStatusResponse | undefined) => {
    if (!status) return 'default';
    if (status.valid) return 'success';
    if (status.configured) return 'warning';
    return 'default';
  };

  const getAuthMethodLabel = (method: string | null) => {
    switch (method) {
      case 'sso_cookies':
        return 'SSO Session Cookies';
      case 'basic_auth':
        return 'Basic Auth (API Token)';
      default:
        return 'Not Configured';
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <ArticleIcon color="primary" />
          <Typography variant="h6">Confluence Integration</Typography>
          <Tooltip title="Refresh status">
            <IconButton size="small" onClick={() => refetchStatus()}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {statusLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Stack spacing={2}>
            {/* SSO Status */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Authentication Status
              </Typography>
              {ssoStatus?.valid ? (
                <Alert severity="success" icon={<CheckCircleIcon />}>
                  Confluence is connected and working
                  {ssoStatus.cookie_names?.length > 0 && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Session cookies: {ssoStatus.cookie_names.join(', ')}
                    </Typography>
                  )}
                </Alert>
              ) : ssoStatus?.configured ? (
                <Alert severity="warning" icon={<ErrorIcon />}>
                  {ssoStatus.error || 'Authentication needs attention'}
                  {ssoStatus.action_required && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Action:</strong> {ssoStatus.action_required}
                    </Typography>
                  )}
                </Alert>
              ) : (
                <Alert severity="info" icon={<InfoIcon />}>
                  Confluence authentication not configured. Your Confluence server requires SSO login.
                  Click "Configure SSO" to set up session cookies.
                </Alert>
              )}
            </Box>

            {/* Configuration Details */}
            {ssoStatus && (
              <>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Configuration
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ gap: 1 }}>
                    <Chip
                      label={getAuthMethodLabel(ssoStatus.auth_method)}
                      color={getStatusColor(ssoStatus)}
                      size="small"
                    />
                    {ssoStatus.base_url && (
                      <Typography variant="caption" color="text.secondary">
                        {ssoStatus.base_url}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </>
            )}

            {/* Error Display */}
            {saveError && (
              <Alert severity="error" onClose={() => setSaveError(null)}>
                {saveError}
              </Alert>
            )}

            {/* Action Buttons */}
            <Divider />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant={ssoStatus?.valid ? 'outlined' : 'contained'}
                onClick={() => setCookieDialogOpen(true)}
                startIcon={<CookieIcon />}
              >
                {ssoStatus?.valid ? 'Update Cookies' : 'Configure SSO'}
              </Button>
              {ssoStatus?.valid && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => clearCookiesMutation.mutate()}
                  disabled={clearCookiesMutation.isPending}
                  startIcon={clearCookiesMutation.isPending ? <CircularProgress size={16} /> : <DeleteIcon />}
                >
                  Clear Cookies
                </Button>
              )}
            </Box>

            {/* Help Text */}
            <Typography variant="body2" color="text.secondary">
              Your Confluence server uses SSO (Single Sign-On) authentication. To connect, you need to:
            </Typography>
            <Box component="ol" sx={{ pl: 2, m: 0, '& li': { mb: 1 } }}>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Open Confluence in your browser and log in via SSO
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Open Developer Tools (F12) → Application → Cookies
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Copy the cookie values (especially JSESSIONID, seraph.confluence)
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Paste them here as JSON format
                </Typography>
              </li>
            </Box>
          </Stack>
        )}
      </CardContent>

      {/* Cookie Configuration Dialog */}
      <Dialog open={cookieDialogOpen} onClose={() => setCookieDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Configure Confluence SSO Session</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            To authenticate with Confluence SSO, you need to capture session cookies from your browser
            after logging in.
          </DialogContentText>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Steps:</strong>
            </Typography>
            <Box component="ol" sx={{ pl: 2, m: 0 }}>
              <li>Click "Open Confluence" below and log in via SSO</li>
              <li>Open Developer Tools (F12 or Cmd+Option+I)</li>
              <li>Go to Application tab → Storage → Cookies</li>
              <li>Find cookies for {ssoStatus?.base_url || 'your Confluence domain'}</li>
              <li>Copy the values for: JSESSIONID, seraph.confluence, crowd.token_key (if present)</li>
              <li>Format them as JSON below</li>
            </Box>
          </Alert>

          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <Button
              variant="outlined"
              onClick={handleOpenConfluence}
              endIcon={<OpenInNewIcon />}
              disabled={!ssoStatus?.base_url}
            >
              Open Confluence
            </Button>
          </Box>

          <TextField
            fullWidth
            multiline
            rows={6}
            label="Session Cookies (JSON)"
            placeholder='{"JSESSIONID": "ABC123...", "seraph.confluence": "..."}'
            value={cookieInput}
            onChange={(e) => setCookieInput(e.target.value)}
            helperText='Enter cookies as a JSON object, e.g.: {"cookie_name": "cookie_value", ...}'
            sx={{ mt: 2 }}
          />

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Session cookies may expire after some time. If Confluence stops working, you'll need to
              log in again and update the cookies.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCookieDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveCookies}
            variant="contained"
            disabled={!cookieInput.trim() || saveCookiesMutation.isPending}
            startIcon={saveCookiesMutation.isPending ? <CircularProgress size={16} /> : null}
          >
            Save Cookies
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
